import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Paper,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { authApiClient } from '../../authApiClient';
import { authService } from '../../services/AuthService';
import SEO from '../../components/SEO';
import { isNetworkError } from '../../utils/errorHandler';
import { resumePassPurchaseAfterLogin } from '../../utils/passPurchaseResume';
import { appendBookingResumeSearchParams, type AuthBookingState } from '../../utils/bookingLoginResume';
import { inviteCompletePath, isInviteRole, type InviteRole } from '../../utils/inviteRegistration';
import {
  getAnalyticsPayload,
  trackRegistrationComplete,
  trackSignupOtpVerified,
  trackTutorOtpVerified,
} from '../../utils/analytics';

interface LocationState {
  phone: string;
  verificationType: 'auth' | 'login' | 'userRegister' | 'hostRegister' | 'tutorRegister' | 'joinOrganization' | 'joinTutor';
  inviteRole?: InviteRole;
  redirectUrl?: string;
  bookingState?: AuthBookingState;
  registrationData?: {
    name: string;
    bandName?: string;
    organizationName?: string;
    joinCode?: string;
    tutorToken?: string;
  };
}

const PENDING_AUTH_STORAGE_KEY = 'pendingAuthVerification';

function readPendingAuth(): LocationState | null {
  try {
    const raw = sessionStorage.getItem(PENDING_AUTH_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as LocationState;
    return parsed?.phone ? parsed : null;
  } catch {
    return null;
  }
}

function writePendingAuth(state: LocationState): void {
  try {
    sessionStorage.setItem(PENDING_AUTH_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // ignore quota / private mode
  }
}

function clearPendingAuth(): void {
  sessionStorage.removeItem(PENDING_AUTH_STORAGE_KEY);
}

const VerifyCodePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [phone, setPhone] = useState('');

  const [verificationType, setVerificationType] = useState<'auth' | 'login' | 'userRegister' | 'hostRegister' | 'tutorRegister' | 'joinOrganization' | 'joinTutor'>('auth');
  const [registrationData, setRegistrationData] = useState<{
    name: string;
    bandName?: string;
    organizationName?: string;
    joinCode?: string;
    tutorToken?: string;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const codeInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Prefer router state; fall back to sessionStorage so HMR / remounts don't dump us on /login.
    const state = (location.state as LocationState | null) ?? readPendingAuth();
    if (!state?.phone) {
      navigate('/login');
      return;
    }
    writePendingAuth(state);
    setPhone(state.phone);
    setVerificationType(state.verificationType || 'auth');
    if (state.registrationData) {
      setRegistrationData(state.registrationData);
    }

    // Check if Web OTP API is supported
    if ('OTPCredential' in window && codeInputRef.current) {
      codeInputRef.current.setAttribute('autocomplete', 'one-time-code');

      const handleCredentialChange = (event: any) => {
        if (event.credential && event.credential.code) {
          const otpCode = event.credential.code;
          setCode(otpCode);
        }
      };

      if (navigator.credentials && 'addEventListener' in navigator.credentials) {
        (navigator.credentials as any).addEventListener('change', handleCredentialChange);
      }
    }
  }, [location, navigate]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const getErrorMessage = (errorMessage: string): string => {
    // Map backend error messages to translation keys
    if (errorMessage.includes('Invalid OTP code')) {
      return t('auth.invalidOtpCode');
    }
    if (errorMessage.includes('Bad Request')) {
      return t('auth.invalidOtpCode');
    }
    if (errorMessage.includes('OTP code has expired')) {
      return t('auth.otpCodeExpired');
    }
    if (errorMessage.includes('User not found')) {
      return t('auth.userNotFound');
    }
    if (errorMessage.includes('Invalid credentials')) {
      return t('auth.invalidCredentials');
    }
    if (errorMessage.includes('User account not confirmed')) {
      return t('auth.userNotConfirmed');
    }
    if (errorMessage.includes('Invalid or expired refresh token')) {
      return t('auth.invalidRefreshToken');
    }
    
    // Handle network errors
    if (isNetworkError(errorMessage)) {
      return t('auth.networkError');
    }
    
    // Handle server errors
    if (errorMessage.includes('Internal server error') || errorMessage.includes('500')) {
      return t('auth.serverError');
    }
    
    // Default fallback
    return errorMessage || t('auth.unknownError');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const trimmed = (code || '').trim();
      if (trimmed.length < 4) {
        setError(t('auth.invalidOtpCode'));
        setIsLoading(false);
        return;
      }
      const analytics = getAnalyticsPayload();
      let response;
      
      switch (verificationType) {
        case 'auth':
        case 'login':
          response = await authApiClient.verifyLoginOtp({ 
            phoneNumber: phone, 
            otp: trimmed,
            analytics,
          });
          break;
        case 'userRegister':
          if (!registrationData) throw new Error(t('verify.registrationDataMissing', 'Registration data is missing'));
          response = await authApiClient.verifyUserRegisterOtp({
            phoneNumber: phone,
            otp: code,
            name: registrationData.name,
            bandName: registrationData.bandName,
            analytics,
          });
          break;
        case 'hostRegister':
          if (!registrationData) throw new Error(t('verify.registrationDataMissing', 'Registration data is missing'));
          response = await authApiClient.verifyHostRegisterOtp({
            phoneNumber: phone,
            otp: code,
            name: registrationData.name,
            organizationName: registrationData.organizationName!,
            analytics,
          });
          break;
        case 'tutorRegister':
          if (!registrationData) throw new Error(t('verify.registrationDataMissing', 'Registration data is missing'));
          response = await authApiClient.verifyTutorRegisterOtp({
            phoneNumber: phone,
            otp: code,
            name: registrationData.name,
            analytics,
          });
          break;
        case 'joinOrganization':
          if (!registrationData?.joinCode) throw new Error(t('verify.joinCodeMissing', 'Join code is missing'));
          response = await authApiClient.verifyLoginOtp({
            phoneNumber: phone,
            otp: trimmed,
            analytics,
          });
          break;
        case 'joinTutor':
          if (!registrationData?.tutorToken) throw new Error(t('verify.tutorTokenMissing', 'Tutor invitation token is missing'));
          response = await authApiClient.verifyLoginOtp({
            phoneNumber: phone,
            otp: trimmed,
            analytics,
          });
          break;
        default:
          throw new Error(t('verify.invalidVerificationType', 'Invalid verification type'));
      }

      // Store tokens in localStorage and update auth state
      localStorage.setItem('access_token', response.accessToken);
      localStorage.setItem('refresh_token', response.refreshToken);
      localStorage.setItem('phone_number', phone);
      if (response.userId) {
        localStorage.setItem('user_id', response.userId);
      }
      // Avoid stale admin-assume headers causing 401s that wipe the new session.
      localStorage.removeItem('assumed_organization_id');
      localStorage.removeItem('assumed_organization_name');
      localStorage.removeItem('assumed_user_id');
      localStorage.removeItem('assumed_user_name');
      localStorage.removeItem('organization_type');

      // If a Buy button stashed a pending pass purchase before sending the user here to log in,
      // resume checkout now instead of following the normal post-login redirect.
      const resumedPassPurchase = await resumePassPurchaseAfterLogin();
      if (resumedPassPurchase) {
        clearPendingAuth();
        return;
      }

      if (verificationType === 'joinOrganization') {
        if (!registrationData?.name) {
          throw new Error(t('verify.registrationDataMissing', 'Registration data is missing'));
        }

        // Join is always applied via completeHostProfile (joinCode), not during OTP verify.
        // profileCompletionRequired may be false for new users who already have USER/TUTOR roles.
        await authApiClient.completeHostProfile({
          accessToken: response.accessToken,
          name: registrationData.name,
          joinCode: registrationData.joinCode,
        });
        const refreshed = await authService.refreshSessionAfterProfileCompletion();
        if (!refreshed) {
          setError(t('authOnboarding.errors.sessionRefreshFailed', 'Could not update your session. Please sign in again.'));
          return;
        }

        clearPendingAuth();
        window.location.href = '/host/organization';
        return;
      }

      if (verificationType === 'joinTutor') {
        if (!registrationData?.name) {
          throw new Error(t('verify.registrationDataMissing', 'Registration data is missing'));
        }

        await authApiClient.completeTutorProfile({
          accessToken: response.accessToken,
          name: registrationData.name,
          invitationToken: registrationData.tutorToken,
        });
        const refreshed = await authService.refreshSessionAfterProfileCompletion();
        if (!refreshed) {
          setError(t('authOnboarding.errors.sessionRefreshFailed', 'Could not update your session. Please sign in again.'));
          return;
        }

        clearPendingAuth();
        window.location.href = '/tutor/listing';
        return;
      }
      
      // Get user roles from token introspection
      const introspectResponse = await authApiClient.introspectToken({ accessToken: response.accessToken });
      localStorage.setItem('user_roles', JSON.stringify(introspectResponse.roles || []));
      localStorage.setItem('token_expires_at', introspectResponse.expiresAt);
      // Keep the E.164 phone used for OTP/refresh; introspect may omit '+'.
      if (introspectResponse.phoneNumber?.startsWith('+')) {
        localStorage.setItem('phone_number', introspectResponse.phoneNumber);
      }
      if (introspectResponse.userId) {
        localStorage.setItem('user_id', introspectResponse.userId);
      }

      // OTP writes tokens to localStorage only; sync AuthService so SPA navigations
      // (e.g. verify → /host/requests) pass ProtectedRoute without a hard reload race.
      authService.syncSessionFromStorage();
      const pendingState = (location.state as LocationState | null) ?? readPendingAuth();
      const inviteRole = isInviteRole(pendingState?.inviteRole) ? pendingState.inviteRole : null;
      if (verificationType === 'tutorRegister' || inviteRole === 'tutor') {
        trackTutorOtpVerified();
      } else if (verificationType === 'hostRegister' || inviteRole === 'host') {
        trackSignupOtpVerified('host');
      } else if (verificationType === 'userRegister' || inviteRole === 'user') {
        trackSignupOtpVerified('user');
      } else if (
        (verificationType === 'auth' || verificationType === 'login') &&
        response.profileCompletionRequired
      ) {
        trackSignupOtpVerified('account');
        // Match server convert on new-user insert (same Meta event_id).
        trackRegistrationComplete('user', { user_id: response.userId });
      }
      if (verificationType === 'userRegister') {
        trackRegistrationComplete('user', { user_id: response.userId });
      } else if (verificationType === 'hostRegister') {
        trackRegistrationComplete('host', { user_id: response.userId });
      }
      clearPendingAuth();
      
      // Redirect to appropriate default page based on user roles
      let redirectUrl;
      
      if (verificationType === 'auth' || verificationType === 'login') {
        if (response.profileCompletionRequired) {
          navigate(inviteRole ? inviteCompletePath(inviteRole) : '/complete-profile', {
            replace: true,
            state: {
              accessToken: response.accessToken,
              redirectUrl: pendingState?.redirectUrl,
              bookingState: pendingState?.bookingState,
              inviteRole: inviteRole ?? undefined,
            },
          });
          return;
        }
        // For login, redirect based on user role
        redirectUrl = introspectResponse.roles.includes('HOST')
          ? '/host/requests'
          : introspectResponse.roles.includes('TUTOR')
            ? '/tutor/listing'
            : '/user/search';
      } else if (verificationType === 'userRegister') {
        // For user registration, go to search page
        redirectUrl = '/user/search';
      } else if (verificationType === 'tutorRegister') {
        // For tutor registration, first complete tutor profile
        redirectUrl = '/tutor/listing';
      } else {
        // For host registration, go to organization page
        redirectUrl = '/host/organization';
      }
      
      // Redirect to previous page if provided
      if (pendingState?.redirectUrl) {
        const url = new URL(pendingState.redirectUrl, window.location.origin);
        appendBookingResumeSearchParams(url, pendingState.bookingState);
        navigate(url.pathname + url.search, { replace: true });
        return;
      }

      // SPA navigate keeps AuthService session; avoid hard reload that re-inits as logged-out.
      navigate(redirectUrl, { replace: true });

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(getErrorMessage(errorMessage));
      console.error(t('verify.verificationError', 'Verification error:'), err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!phone || isLoading || resendCooldown > 0) return;
    setError('');
    setIsLoading(true);
    try {
      await authApiClient.initiateLogin({ phoneNumber: phone });
      setResendCooldown(30);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(getErrorMessage(errorMessage));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <SEO 
        title={t('verify.title')}
        noindex={true}
      />
      <Container component="main" maxWidth="xs">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
            <Typography component="h1" variant="h5" align="center" gutterBottom>
              {t('verify.title', 'Verify Your Phone')}
            </Typography>
            <Typography variant="body2" align="center" sx={{ mb: 3 }}>
              {t('verify.enterCode', 'Enter the verification code sent to {{phone}}', { phone })}
            </Typography>
            
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
              <TextField
                required
                fullWidth
                label={t('verify.verificationCode', 'Verification Code')}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                disabled={isLoading}
                inputRef={codeInputRef}
                inputProps={{
                  autoComplete: 'one-time-code',
                  'data-testid': 'verification-code-input'
                }}
                sx={{ mb: 2 }}
              />
              {error && (
                <Typography color="error" sx={{ mb: 2 }}>
                  {error}
                </Typography>
              )}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={isLoading}
                sx={{ mb: 2 }}
              >
                {isLoading ? t('common.loading', 'Loading...') : t('verify.verifyButton', 'Verify')}
              </Button>
              <Button
                fullWidth
                variant="text"
                onClick={() => navigate('/login')}
                disabled={isLoading}
              >
                {t('verify.backToLogin', 'Back to Login')}
              </Button>
              <Button
                fullWidth
                variant="text"
                onClick={handleResendCode}
                disabled={isLoading || resendCooldown > 0}
                sx={{ mt: 1 }}
              >
                {resendCooldown > 0
                  ? t('verify.resendIn', 'Resend code in {{seconds}}s', { seconds: resendCooldown })
                  : t('verify.resend', 'Resend code')}
              </Button>
            </Box>
          </Paper>
        </Box>
      </Container>
    </>
  );
};

export default VerifyCodePage;
